import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StartCompany } from '../interfaces/start';
import {
  IWhatsappBlockedItem,
  IPhoneBlockedItem,
  IEmailBlockedItem,
} from '../interfaces/block-contact.interface';

export interface CompanyBlockData {
  companyId: string;
  blockedSendersWhatsapp: IWhatsappBlockedItem[];
  clientPhones: IPhoneBlockedItem[];
  blockedSenders: IEmailBlockedItem[];
}

@Injectable({
  providedIn: 'root',
})
export class CompanyBlockDataService {
  private blockDataMap = new Map<string, CompanyBlockData>();
  private blockDataUpdated$ = new BehaviorSubject<string | null>(null);

  public readonly onBlockDataUpdated$ = this.blockDataUpdated$.asObservable();

  initializeFromCompanies(companies: StartCompany[]): void {
    companies.forEach((company) => {
      const json = company.json || {}
      this.blockDataMap.set(company.companyId, {
        companyId: company.companyId,
        blockedSendersWhatsapp: json.blockedSendersWhatsapp || [],
        clientPhones: json.clientPhones || [],
        blockedSenders: json.blockedSenders || [],
      });
    });
    this.blockDataUpdated$.next('initialized');
  }

  getBlockData(companyId: string): CompanyBlockData | undefined {
    return this.blockDataMap.get(companyId);
  }

  isWhatsappBlocked(companyId: string, phone: string): boolean {
    const data = this.blockDataMap.get(companyId);
    if (!data) return false;

    const normalizedPhone = this.normalizePhone(phone);
    return data.blockedSendersWhatsapp.some(
      (item) => this.normalizePhone(item.number) === normalizedPhone
    );
  }

  isPhoneBlocked(companyId: string, phone: string): boolean {
    const data = this.blockDataMap.get(companyId);
    if (!data) return false;

    const normalizedPhone = this.normalizePhone(phone);
    return data.clientPhones.some(
      (item) =>
        this.normalizePhone(item.id) === normalizedPhone ||
        this.normalizePhone(item.id.replace(/^55/, '')) === normalizedPhone
    );
  }

  isEmailBlocked(companyId: string, email: string): boolean {
    const data = this.blockDataMap.get(companyId);
    if (!data) return false;

    const normalizedEmail = email.toLowerCase();
    return data.blockedSenders.some(
      (item) =>
        item.source === 'email' &&
        item.senderId.toLowerCase() === normalizedEmail
    );
  }

  addWhatsappBlock(companyId: string, item: IWhatsappBlockedItem): void {
    const data = this.blockDataMap.get(companyId);
    if (!data) return;

    const normalizedPhone = this.normalizePhone(item.number);
    const exists = data.blockedSendersWhatsapp.some(
      (existing) => this.normalizePhone(existing.number) === normalizedPhone
    );

    if (!exists) {
      data.blockedSendersWhatsapp.push(item);
      this.blockDataUpdated$.next(companyId);
    }
  }

  removeWhatsappBlock(companyId: string, phone: string): void {
    const data = this.blockDataMap.get(companyId);
    if (!data) return;

    const normalizedPhone = this.normalizePhone(phone);
    data.blockedSendersWhatsapp = data.blockedSendersWhatsapp.filter(
      (item) => this.normalizePhone(item.number) !== normalizedPhone
    );
    this.blockDataUpdated$.next(companyId);
  }

  addPhoneBlock(companyId: string, item: IPhoneBlockedItem): void {
    const data = this.blockDataMap.get(companyId);
    if (!data) return;

    const normalizedPhone = this.normalizePhone(item.id);
    const exists = data.clientPhones.some(
      (existing) => this.normalizePhone(existing.id) === normalizedPhone
    );

    if (!exists) {
      data.clientPhones.push(item);
      this.blockDataUpdated$.next(companyId);
    }
  }

  removePhoneBlock(companyId: string, phone: string): void {
    const data = this.blockDataMap.get(companyId);
    if (!data) return;

    const normalizedPhone = this.normalizePhone(phone);
    data.clientPhones = data.clientPhones.filter(
      (item) => this.normalizePhone(item.id) !== normalizedPhone
    );
    this.blockDataUpdated$.next(companyId);
  }

  addEmailBlock(companyId: string, item: IEmailBlockedItem): void {
    const data = this.blockDataMap.get(companyId);
    if (!data) return;

    const normalizedEmail = item.senderId.toLowerCase();
    const exists = data.blockedSenders.some(
      (existing) => existing.senderId.toLowerCase() === normalizedEmail
    );

    if (!exists) {
      data.blockedSenders.push(item);
      this.blockDataUpdated$.next(companyId);
    }
  }

  removeEmailBlock(companyId: string, email: string): void {
    const data = this.blockDataMap.get(companyId);
    if (!data) return;

    const normalizedEmail = email.toLowerCase();
    data.blockedSenders = data.blockedSenders.filter(
      (item) => item.senderId.toLowerCase() !== normalizedEmail
    );
    this.blockDataUpdated$.next(companyId);
  }

  private normalizePhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }
}
